import {
  RISK_DESCRIPTION_MAX,
  RISK_OWNER_LABEL_MAX,
  RISK_TITLE_MAX,
  type CreateRiskRequest,
  type RiskSeverity,
  type RiskStatus,
  type RoomItemVisibility,
  type RoomRisk,
  type UpdateRiskRequest,
} from '@dataroom/shared';

/** The owner picker's value for "someone else" (a free-text owner). */
export const OTHER_OWNER = '__other__';

/**
 * The risk dialog's form. The owner is ONE choice — nobody (''), a person from
 * the room's owner options (their actor id), or someone else (`OTHER_OWNER` +
 * `ownerText`) — so the form can never hold both a person and a name, which the
 * API refuses.
 */
export interface RiskFormState {
  title: string;
  description: string;
  severity: RiskSeverity;
  ownerChoice: string;
  ownerText: string;
  /** 'YYYY-MM-DD' or ''. */
  dueDate: string;
  status: RiskStatus;
  visibility: RoomItemVisibility;
}

export const EMPTY_RISK_FORM: RiskFormState = {
  title: '',
  description: '',
  severity: 'medium',
  ownerChoice: '',
  ownerText: '',
  dueDate: '',
  status: 'open',
  visibility: 'deal_team',
};

/** The edit form seeded from an existing entry (the admin's projection). */
export function riskFormFrom(risk: RoomRisk): RiskFormState {
  const label = risk.ownerLabel?.trim() ?? '';
  return {
    title: risk.title,
    description: risk.description ?? '',
    severity: risk.severity,
    ownerChoice: risk.ownerActorId ? risk.ownerActorId : label ? OTHER_OWNER : '',
    ownerText: risk.ownerActorId ? '' : label,
    dueDate: risk.dueDate ?? '',
    status: risk.status,
    visibility: risk.visibility ?? 'deal_team',
  };
}

/** The owner as the API takes it: exactly one of the two fields set, or neither. */
function ownerFields(f: RiskFormState): { ownerActorId: string | null; ownerLabel: string | null } {
  if (f.ownerChoice === OTHER_OWNER) {
    const text = f.ownerText.trim();
    return { ownerActorId: null, ownerLabel: text || null };
  }
  return { ownerActorId: f.ownerChoice || null, ownerLabel: null };
}

/** Why the form can't be saved yet, or null when it can. */
export function riskFormProblem(f: RiskFormState): string | null {
  if (!f.title.trim()) return 'Give the entry a title.';
  if (f.title.trim().length > RISK_TITLE_MAX) {
    return `Keep the title to ${RISK_TITLE_MAX} characters.`;
  }
  if (f.description.length > RISK_DESCRIPTION_MAX) {
    return `Keep the description to ${RISK_DESCRIPTION_MAX} characters.`;
  }
  if (f.ownerChoice === OTHER_OWNER && !f.ownerText.trim()) {
    return "Type the owner's name, or pick someone from the room.";
  }
  if (f.ownerText.trim().length > RISK_OWNER_LABEL_MAX) {
    return `Keep the owner's name to ${RISK_OWNER_LABEL_MAX} characters.`;
  }
  return null;
}

/** POST body for a new entry: empty optional fields are left out, and the owner
 * goes as a person OR as text — never both. */
export function createRiskBody(f: RiskFormState): CreateRiskRequest {
  const owner = ownerFields(f);
  const description = f.description.trim();
  return {
    title: f.title.trim(),
    severity: f.severity,
    status: f.status,
    visibility: f.visibility,
    ...(description ? { description } : {}),
    ...(owner.ownerActorId ? { ownerActorId: owner.ownerActorId } : {}),
    ...(owner.ownerLabel ? { ownerLabel: owner.ownerLabel } : {}),
    ...(f.dueDate ? { dueDate: f.dueDate } : {}),
  };
}

/**
 * PATCH body for an edit: ONLY the fields that changed, so an untouched owner who
 * has since left the room is never re-sent (the API would refuse them). A changed
 * owner sends both owner fields — the new one set and the other null — and a
 * cleared description or due date is sent as null. `{}` means nothing changed.
 */
export function updateRiskBody(before: RiskFormState, after: RiskFormState): UpdateRiskRequest {
  const body: UpdateRiskRequest = {};
  if (after.title.trim() !== before.title.trim()) body.title = after.title.trim();
  if (after.description.trim() !== before.description.trim()) {
    body.description = after.description.trim() || null;
  }
  if (after.severity !== before.severity) body.severity = after.severity;
  if (after.status !== before.status) body.status = after.status;
  if (after.visibility !== before.visibility) body.visibility = after.visibility;
  if (after.dueDate !== before.dueDate) body.dueDate = after.dueDate || null;
  const a = ownerFields(after);
  const b = ownerFields(before);
  if (a.ownerActorId !== b.ownerActorId || a.ownerLabel !== b.ownerLabel) {
    body.ownerActorId = a.ownerActorId;
    body.ownerLabel = a.ownerLabel;
  }
  return body;
}
